/**
 * Nav collection grid — client-side filters (v2)
 * Filters instantly from DOM product data; enriches from products.json in background.
 */
(function () {
  'use strict';

  const MOBILE_MAX_WIDTH = 999;
  const APPLY_DEBOUNCE_MS = 0;
  const CATALOG_BATCH = 250;
  const CARD_FETCH_BATCH = 20;
  const PRODUCT_CARD_CONCURRENCY = 5;
  const FILTER_PARAM_KEYS = ['availability', 'vendor', 'product_type', 'size', 'color', 'tag', 'price_min', 'price_max', 'sort_by'];
  const EDITOR_QUERY_KEYS = ['preview_theme_id', 'oseid', 'key', 'v', '_fd', 'pb'];
  const NAV_JUMP_PARAM = 'nav_col_jump';

  function handleize(str) {
    let value = String(str || '');
    // Match Shopify handle behavior closer for accented characters.
    if (typeof value.normalize === 'function') {
      value = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    value = value.replace(/ß/g, 'ss');
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function normalizeColorKey(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/colour/g, 'color')
      .replace(/&/g, ' and ')
      .replace(/\bcolors?\b/g, '')
      .replace(/\band\b/g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .trim();
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value || ''));
    }
    return String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatLabel(handle) {
    return String(handle || '')
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  function formatSizeLabel(handle) {
    const raw = String(handle || '').toLowerCase().trim();
    const decimalMatch = raw.match(/^(\d+)-(\d+)([a-z]+)$/);
    if (decimalMatch) {
      return `${decimalMatch[1]}.${decimalMatch[2]}${decimalMatch[3]}`;
    }
    return formatLabel(raw);
  }

  const HIDDEN_FACET_HANDLES = new Set(['not-specified', 'unspecified', 'n-a', 'na']);

  function isHiddenFacetValue(handle, label) {
    const normalizedHandle = String(handle || '').toLowerCase().trim();
    const normalizedLabel = String(label || '').toLowerCase().trim();
    if (!normalizedHandle && !normalizedLabel) return true;
    if (HIDDEN_FACET_HANDLES.has(normalizedHandle)) return true;
    if (normalizedLabel === 'not specified' || normalizedLabel === 'unspecified') return true;
    return false;
  }

  const SIZE_WORD_ORDER = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '2xl', '3xl', '4xl', '5xl'];
  const SIZE_UNIT_ORDER = ['s', 'ml', 'g', 'caps'];

  function parseSizeSortKey(value) {
    const handle = String(value || '').toLowerCase().trim();
    const normalized = handle.replace(/-/g, ' ').replace(/\s+/g, ' ').trim();

    const wordIdx = SIZE_WORD_ORDER.indexOf(handle);
    if (wordIdx !== -1) {
      return { kind: 'word', num: wordIdx, unit: '', raw: handle };
    }

    const splitMatch = normalized.match(/^(\d+)(?:\s+(\d+))?\s*([a-z]+)?$/);
    if (splitMatch) {
      let num = parseFloat(splitMatch[1], 10);
      if (splitMatch[2]) {
        num = parseFloat(`${splitMatch[1]}.${splitMatch[2]}`, 10);
      }
      return { kind: 'numeric', num, unit: splitMatch[3] || '', raw: handle };
    }

    const embedded = normalized.match(/(\d+(?:\.\d+)?)\s*([a-z]+)?/);
    return {
      kind: 'numeric',
      num: embedded ? parseFloat(embedded[1], 10) : 0,
      unit: embedded && embedded[2] ? embedded[2] : normalized.replace(/[\d.\s]/g, ''),
      raw: handle
    };
  }

  function compareSizeUnits(unitA, unitB) {
    const a = String(unitA || '').toLowerCase();
    const b = String(unitB || '').toLowerCase();
    const rankA = SIZE_UNIT_ORDER.indexOf(a);
    const rankB = SIZE_UNIT_ORDER.indexOf(b);
    const orderA = rankA === -1 ? SIZE_UNIT_ORDER.length : rankA;
    const orderB = rankB === -1 ? SIZE_UNIT_ORDER.length : rankB;
    if (orderA !== orderB) return orderA - orderB;
    if (rankA === -1 && rankB === -1) return a.localeCompare(b);
    return 0;
  }

  function compareSizeValues(a, b) {
    const ka = parseSizeSortKey(a);
    const kb = parseSizeSortKey(b);

    if (ka.kind === 'word' && kb.kind !== 'word') return -1;
    if (kb.kind === 'word' && ka.kind !== 'word') return 1;
    if (ka.kind === 'word' && kb.kind === 'word') return ka.num - kb.num;

    const unitCmp = compareSizeUnits(ka.unit, kb.unit);
    if (unitCmp !== 0) return unitCmp;

    if (ka.num !== kb.num) return ka.num - kb.num;
    return ka.raw.localeCompare(kb.raw);
  }

  function isMobile() {
    return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
  }

  function stripCardHtml(html) {
    if (!html) return '';
    return String(html)
      .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');
  }

  function extractCardListItems(html) {
    const stripped = stripCardHtml(html);
    if (!stripped) return '';

    const template = document.createElement('template');
    template.innerHTML = stripped;

    const directCards = template.content.querySelectorAll('li[data-product-handle]');
    if (directCards.length) {
      return Array.from(directCards)
        .map((li) => li.outerHTML)
        .join('');
    }

    return '';
  }

  function filterCardListItemsByHandles(html, handles) {
    if (!html) return '';
    const allowed = new Set((handles || []).map((h) => String(h || '').trim()).filter(Boolean));
    if (!allowed.size) return '';

    const template = document.createElement('template');
    template.innerHTML = html;
    const kept = [];
    template.content.querySelectorAll('li[data-product-handle]').forEach((li) => {
      const handle = li.getAttribute('data-product-handle');
      if (!handle || !allowed.has(handle)) return;
      kept.push(li.outerHTML);
    });
    return kept.join('');
  }

  function isThemeEditor() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function mergeEditorSearchParams(params) {
    if (!isThemeEditor()) return params;
    const current = new URLSearchParams(window.location.search);
    EDITOR_QUERY_KEYS.forEach((key) => {
      const value = current.get(key);
      if (value) params.set(key, value);
    });
    return params;
  }

  function collectFilterParamKeys(config, params) {
    const keys = new Set(FILTER_PARAM_KEYS);
    ((config && config.tagFilterGroups) || []).forEach((group) => {
      keys.add('tag_' + group.id);
    });
    if (params) {
      params.forEach((_, key) => {
        if (key.startsWith('tag_')) keys.add(key);
      });
    }
    return keys;
  }

  class NavColFilters {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.section = document.querySelector('.nav-col-' + sectionId + '-grid');
      this.filtersEl = document.getElementById('custom-filters-' + sectionId);
      this.grid = this.section ? this.section.querySelector('#product-grid') : null;
      this.tagsEl = document.getElementById('active-filters-tags-' + sectionId);
      this.resultsEl = document.getElementById('filter-results-count');
      this.noResultsEl = document.getElementById('no-results-message-' + sectionId);
      this.config = null;
      this.products = [];
      this.byHandle = new Map();
      this.active = new Map();
      this.fullCatalogLoaded = false;
      this.catalogPromise = null;
      this.applyTimer = null;
      this.filtered = false;
      this._optionsBuilt = false;
      this._applying = false;
      this._pendingApply = null;
      this.gridHydrated = false;
      this.gridHydrationPromise = null;
      this._cleanups = [];
      this._suppress = false;
      this.debug = window.NavColFilterDebug
        ? window.NavColFilterDebug.forSection(sectionId)
        : { log: function () {}, warn: function () {}, error: function () {}, time: function (_l, fn) { return typeof fn === 'function' ? fn() : undefined; }, snapshot: function () {}, audit: function () {}, attach: function () {} };

      if (!this.section || !this.filtersEl || !this.grid || this.grid.tagName !== 'UL') {
        this.debug.error('init-abort', {
          section: !!this.section,
          filtersEl: !!this.filtersEl,
          grid: !!this.grid,
          gridTag: this.grid ? this.grid.tagName : null
        });
        return;
      }

      const configEl = document.getElementById('NavColGridConfig-' + sectionId);
      if (configEl) {
        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (error) {
          console.warn('[NavColFilters] config parse failed', error);
          this.debug.error('config-parse-failed', { message: error.message });
        }
      }

      this.debug.log('init-start', { sectionId: sectionId });
      this.filtersEl.classList.add('is-catalog-loading');

      this.bindEvents();
      this.debug.attach(this);
      this.debug.audit(this.section);

      this.prepareCatalog()
        .then(() => {
          this.filtersEl.classList.remove('is-catalog-loading');
          this.filtersEl.classList.add('catalog-synced');
          this.pushDebugSnapshot();
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => this.loadFromUrl());
          });
        })
        .catch((error) => {
          console.warn('[NavColFilters] catalog prepare failed', error);
          this.filtersEl.classList.remove('is-catalog-loading');
          this.filtersEl.classList.add('catalog-synced');
          this.indexFromDom();
          if (!this._optionsBuilt) this.syncFilterOptions();
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => this.loadFromUrl());
          });
        });
    }

    async prepareCatalog() {
      this.indexFromDom();
      this.syncFilterOptions();
      this.syncTagBoxes();
      this.syncGroupVisibility();
      this.log('catalog-dom-ready', { count: this.products.length });

      if (this.isSearchPage()) {
        this.enforceSearchResultsVisibility();
      }

      if (this.needsFullCatalog()) {
        // Keep initial interaction instant; expand filter options in background.
        this.loadFullCatalog().catch((error) => {
          console.warn('[NavColFilters] full catalog preload failed', error);
        });
      } else {
        this.fullCatalogLoaded = !this.isSearchPage();
        this.log('catalog-skip', { reason: 'all products already on page', count: this.products.length });
      }
    }

    ensureCatalogReady() {
      return Promise.resolve(this.products);
    }

    async ensureGridHydrated() {
      this.gridHydrated = true;
    }

    destroy() {
      window.clearTimeout(this.applyTimer);
      this.applyTimer = null;
      while (this._cleanups.length) {
        const fn = this._cleanups.pop();
        try {
          fn();
        } catch (error) {
          /* ignore */
        }
      }
    }

    track(fn) {
      if (typeof fn === 'function') this._cleanups.push(fn);
    }

    log(event, detail) {
      if (this.debug && this.debug.log) this.debug.log(event, detail);
      if (!this.config || !this.config.filterDebug) return;
      console.info('[NavColFilters]', event, detail || '');
    }

    pushDebugSnapshot(extra) {
      if (!this.debug || !this.debug.snapshot) return;
      const visible = this.grid
        ? [...this.grid.querySelectorAll('li[data-product-handle]')].filter(
            (li) => !li.classList.contains('nav-col-filter-hidden')
          ).length
        : 0;
      const activeSummary = [...this.active.entries()]
        .map(([k, v]) => {
          if (v instanceof Set) return k + '=' + [...v].join('|');
          if (k === 'price') return 'price=' + v.min + '-' + v.max;
          return k;
        })
        .join(', ');
      this.debug.snapshot(
        Object.assign(
          {
            gridItems: this.grid ? this.grid.querySelectorAll('li[data-product-handle]').length : 0,
            indexed: this.products.length,
            visible: visible,
            activeSummary: activeSummary || 'none',
            filtered: this.filtered
          },
          extra || {}
        )
      );
    }

    getExpectedCount() {
      const n = parseInt(this.config && this.config.collectionProductCount, 10);
      return Number.isFinite(n) && n > 0 ? n : 0;
    }

    needsFullCatalog() {
      if (this.isSearchPage()) return false;
      const expected = this.getExpectedCount();
      if (!expected) return true;
      return this.products.length < expected;
    }

    /* ——— Product index ——— */

    addProduct(product) {
      if (!product || !product.handle) return;
      const existing = this.byHandle.get(product.handle);
      if (existing) {
        Object.assign(existing, product);
        return;
      }
      this.byHandle.set(product.handle, product);
      this.products.push(product);
    }

    parseCardElement(card) {
      const price = parseFloat(String(card.dataset.price || '').replace(/,/g, '')) || 0;
      return {
        title: card.dataset.title || '',
        vendor: handleize(card.dataset.vendor || ''),
        type: handleize(card.dataset.productType || ''),
        tags: (card.dataset.tags || '').split(',').map(handleize).filter(Boolean),
        colors: (card.dataset.colors || '').split(',').map(normalizeColorKey).filter(Boolean),
        sizes: (card.dataset.sizes || '').split(',').map(handleize).filter(Boolean),
        availability: card.dataset.availability || 'in-stock',
        price,
        vendorLabel: card.dataset.vendor || '',
        typeLabel: card.dataset.productType || ''
      };
    }

    parseJsonProduct(raw) {
      const tags = (Array.isArray(raw.tags) ? raw.tags : String(raw.tags || '').split(','))
        .map((t) => handleize(String(t).trim()))
        .filter(Boolean);
      const colors = [];
      const sizes = [];
      const seenColors = new Set();
      const seenSizes = new Set();
      let minPrice = Infinity;

      (raw.variants || []).forEach((variant) => {
        const p = parseFloat(variant.price);
        if (!Number.isNaN(p) && p < minPrice) minPrice = p;
      });

      (raw.options || []).forEach((option) => {
        const name = String(option.name || '').toLowerCase();
        (option.values || []).forEach((value) => {
          const key = handleize(value);
          if (name === 'color' || name === 'colour') {
            const n = normalizeColorKey(key);
            if (n && !seenColors.has(n)) {
              seenColors.add(n);
              colors.push(n);
            }
          } else if (name.includes('size')) {
            if (key && key !== 'default-title' && !seenSizes.has(key)) {
              seenSizes.add(key);
              sizes.push(key);
            }
          }
        });
      });

      const available =
        raw.available === true || (raw.variants || []).some((v) => v.available);

      return {
        handle: raw.handle,
        title: raw.title || '',
        vendor: handleize(raw.vendor || ''),
        vendorLabel: raw.vendor || '',
        type: handleize(raw.product_type || ''),
        typeLabel: raw.product_type || '',
        tags,
        colors,
        sizes,
        availability: available ? 'in-stock' : 'out-of-stock',
        price: minPrice === Infinity ? 0 : minPrice / 100
      };
    }

    indexFromDom() {
      this.products = [];
      this.byHandle.clear();
      this.refreshProductIndexFromDom();
      this.log('dom-index', { count: this.products.length });
    }

    refreshProductIndexFromDom() {
      if (!this.grid) return;
      this.grid.querySelectorAll('li[data-product-handle]').forEach((li) => {
        const card = li.querySelector('.collection-product-card');
        if (!card) return;
        const data = this.parseCardElement(card);
        data.handle = li.getAttribute('data-product-handle');
        this.addProduct(data);
      });
    }

    async loadFullCatalog() {
      if (this.catalogPromise) return this.catalogPromise;
      if (this.isSearchPage()) {
        this.fullCatalogLoaded = false;
        return this.products;
      }
      if (!this.config || !this.config.collectionUrl) return this.products;
      if (!this.needsFullCatalog()) {
        this.fullCatalogLoaded = true;
        this.log('catalog-skip', { reason: 'all products already on page' });
        return this.products;
      }

      this.catalogPromise = (async () => {
        const base = this.config.collectionUrl.replace(/\/$/, '');
        const sort = new URLSearchParams(window.location.search).get('sort_by');
        let page = 1;
        const fetched = [];

        while (page <= 40) {
          const url = new URL(base + '/products.json', window.location.origin);
          url.searchParams.set('limit', String(CATALOG_BATCH));
          url.searchParams.set('page', String(page));
          if (sort) url.searchParams.set('sort_by', sort);
          if (isThemeEditor()) mergeEditorSearchParams(url.searchParams);

          const res = await fetch(url.toString());
          if (!res.ok) throw new Error('products.json ' + res.status);
          const data = await res.json();
          const batch = data.products || [];
          for (let i = 0; i < batch.length; i += 1) {
            fetched.push(this.parseJsonProduct(batch[i]));
            // Yield periodically to avoid main-thread stutter on large catalogs.
            if (i > 0 && i % 40 === 0) {
              await new Promise((resolve) => window.setTimeout(resolve, 0));
            }
          }
          if (batch.length < CATALOG_BATCH) break;
          page += 1;
        }

        if (fetched.length) {
          this.products = [];
          this.byHandle.clear();
          fetched.forEach((p) => this.addProduct(p));
        }

        this.fullCatalogLoaded = true;
        this.syncFilterOptions();
        this.syncTagBoxes();
        this.syncGroupVisibility();
        this.log('catalog-ready', { count: this.products.length });

        if (this.active.size > 0) {
          this.apply({ resetPage: false });
        }

        return this.products;
      })();

      try {
        return await this.catalogPromise;
      } catch (error) {
        console.warn('[NavColFilters] catalog load failed, using DOM index', error);
        this.fullCatalogLoaded = true;
        return this.products;
      }
    }

    /* ——— Filter matching ——— */

    isSearchPage() {
      if (this.config && this.config.isSearchPage) return true;
      return /\/search\b/.test(window.location.pathname || '');
    }

    getSearchQuery() {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('q');
      if (fromUrl) return String(fromUrl).trim();
      return String((this.config && this.config.searchQuery) || '').trim();
    }

    getSearchResultHandleSet() {
      const handles = Array.isArray(this.config && this.config.searchResultHandles)
        ? this.config.searchResultHandles
        : [];
      return new Set(handles.map((h) => String(h || '').trim()).filter(Boolean));
    }

    isInSearchResults(product) {
      if (!this.isSearchPage() || !product || !product.handle) return true;
      if (!this.matchesSearch(product)) return false;
      const searchHandles = this.getSearchResultHandleSet();
      if (searchHandles.size) return searchHandles.has(product.handle);
      return !!this.getSearchQuery();
    }

    matchesSearch(product) {
      const query = this.getSearchQuery().toLowerCase();
      if (!query) return false;
      const tokens = query.split(/\s+/).filter(Boolean);
      const haystack = [product.title || '', product.vendorLabel || '', product.typeLabel || '']
        .join(' ')
        .toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    }

    getCatalogPool() {
      if (!this.isSearchPage()) return this.products;
      const searchHandles = this.getSearchResultHandleSet();
      if (searchHandles.size) {
        return this.products.filter((p) => searchHandles.has(p.handle));
      }
      if (this.getSearchQuery()) {
        return this.products.filter((p) => this.matchesSearch(p));
      }
      return [];
    }

    getTagHandlesForFilter(filterKey, selectedValues) {
      const group = (this.config.tagFilterGroups || []).find(
        (g) => 'tag_' + g.id === filterKey
      );
      const selected = Array.from(selectedValues || []).map(handleize).filter(Boolean);

      if (!selected.length) return [];

      // Backward compatibility: old URLs/value used group id.
      if (group && group.tagHandles && group.tagHandles.length) {
        const groupIdHandle = handleize(group.id);
        if (selected.includes(groupIdHandle)) {
          return group.tagHandles.map(handleize).filter(Boolean);
        }
      }

      return selected;
    }

    matches(product) {
      if (this.isSearchPage() && !this.isInSearchResults(product)) {
        return false;
      }
      for (const [key, val] of this.active) {
        if (key === 'price') {
          const { min, max } = val;
          if (product.price < min || product.price > max) return false;
          continue;
        }
        if (key === 'vendor') {
          const wanted = Array.from(val).map(handleize);
          if (!wanted.includes(product.vendor)) return false;
          continue;
        }
        if (key === 'product_type') {
          const wanted = Array.from(val).map(handleize);
          if (!wanted.includes(product.type)) return false;
          continue;
        }
        if (key === 'availability') {
          if (!Array.from(val).includes(product.availability)) return false;
          continue;
        }
        if (key === 'size') {
          const wanted = Array.from(val).map(handleize);
          if (!wanted.some((s) => product.sizes.includes(s))) return false;
          continue;
        }
        if (key === 'color') {
          const wanted = Array.from(val)
            .flatMap((v) => String(v).split('|'))
            .map(normalizeColorKey)
            .filter(Boolean);
          if (!wanted.some((c) => product.colors.includes(c))) return false;
          continue;
        }
        if (key.startsWith('tag_')) {
          const matchTags = this.getTagHandlesForFilter(key, val);
          if (!matchTags.length) return false;
          if (!matchTags.some((t) => product.tags.includes(t))) return false;
          continue;
        }
      }
      return true;
    }

    getAllMatchingHandles() {
      if (this.fullCatalogLoaded && !this.isSearchPage()) {
        return this.getMatches().map((p) => p.handle);
      }
      return this.getMatchingHandlesFromDom();
    }

    getMatches() {
      return this.getCatalogPool().filter((p) => this.matches(p));
    }

    getMatchingHandlesFromDom() {
      const handles = [];
      if (!this.grid) return handles;
      this.grid.querySelectorAll('li[data-product-handle]').forEach((li) => {
        const card = li.querySelector('.collection-product-card');
        if (!card) return;
        const data = this.parseCardElement(card);
        if (this.matches(data)) {
          handles.push(li.getAttribute('data-product-handle'));
        }
      });
      return handles;
    }

    /* ——— Active filter state ——— */

    readActiveFromDom() {
      const next = new Map();
      this.filtersEl.querySelectorAll('.filter-checkbox:checked').forEach((input) => {
        if (!next.has(input.name)) next.set(input.name, new Set());
        next.get(input.name).add(input.value);
      });

      const minEl = this.filtersEl.querySelector('input[name="price_min"]');
      const maxEl = this.filtersEl.querySelector('input[name="price_max"]');
      const min = minEl ? parseFloat(minEl.value) || 0 : 0;
      const max = maxEl ? parseFloat(maxEl.value) || Infinity : Infinity;
      if (min > 0 || max < Infinity) {
        next.set('price', { min, max });
      }

      this.active = next;
      this.syncTagBoxState();
    }

    collectActiveFromDom() {
      const next = new Map();
      if (!this.filtersEl) return next;

      this.filtersEl.querySelectorAll('.filter-checkbox:checked').forEach((input) => {
        if (!next.has(input.name)) next.set(input.name, new Set());
        next.get(input.name).add(input.value);
      });

      const minEl = this.filtersEl.querySelector('input[name="price_min"]');
      const maxEl = this.filtersEl.querySelector('input[name="price_max"]');
      const min = minEl ? parseFloat(minEl.value) || 0 : 0;
      const max = maxEl ? parseFloat(maxEl.value) || Infinity : Infinity;
      if (min > 0 || max < Infinity) {
        next.set('price', { min, max });
      }

      return next;
    }

    syncActiveFiltersFromDom() {
      this.readActiveFromDom();
    }

    syncTagBoxState() {
      this.filtersEl.querySelectorAll('.filter-option--tag-box, .filter-option--size-box').forEach((option) => {
        const input = option.querySelector('.filter-checkbox');
        option.classList.toggle('is-selected', !!(input && input.checked));
      });
    }

    clearAll() {
      this._suppress = true;
      this.filtersEl.querySelectorAll('.filter-checkbox').forEach((c) => {
        c.checked = false;
      });
      this.filtersEl.querySelectorAll('.price-input').forEach((i) => {
        i.value = '';
      });
      this._suppress = false;
      this.active.clear();
      this.restore();
    }

    /* ——— Apply / restore ——— */

    scheduleApply(options) {
      window.clearTimeout(this.applyTimer);
      this.applyTimer = window.setTimeout(() => {
        window.requestAnimationFrame(() => {
          this.readActiveFromDom();
          if (this.active.size === 0) this.restore();
          else this.applyFilters(options || { resetPage: true });
        });
      }, APPLY_DEBOUNCE_MS);
    }

    applyFilters(options) {
      this.readActiveFromDom();
      if (this.active.size === 0) {
        this.restore();
        return;
      }
      this.apply(options || { resetPage: true });
    }

    setItemVisible(li, visible) {
      li.classList.toggle('nav-col-filter-hidden', !visible);
      // Also enforce inline visibility to recover from older scripts that used style.display.
      li.style.display = visible ? '' : 'none';
    }

    apply({ resetPage = true } = {}) {
      if (this._applying) {
        this.debug.warn('apply-skipped', { reason: 'already-applying' });
        this._pendingApply = { resetPage };
        return Promise.resolve();
      }
      this._applying = true;

      const runApply = async () => {
        try {
          await this.debug.time('apply', async () => {
            const allMatches = this.getAllMatchingHandles();
            const pageMatches = this.getMatchingHandlesFromDom();
            let handlesToShow = pageMatches;

            this.log('apply', {
              active: Object.fromEntries(
                [...this.active.entries()].map(([k, v]) => [k, v instanceof Set ? [...v] : v])
              ),
              matchesAllPages: allMatches.length,
              matchesCurrentPage: pageMatches.length
            });

            if (this.fullCatalogLoaded && allMatches.length > 0 && !this.isSearchPage()) {
              const perPageRaw = parseInt(this.config && this.config.productsPerPage, 10);
              const perPage = Number.isFinite(perPageRaw) && perPageRaw > 0 ? perPageRaw : 16;
              const targetCount = Math.min(perPage, allMatches.length);
              handlesToShow = allMatches.slice(0, targetCount);

              const gridHandleSet = new Set(
                [...this.grid.querySelectorAll('li[data-product-handle]')]
                  .map((li) => li.getAttribute('data-product-handle'))
                  .filter(Boolean)
              );
              const missingInGrid = handlesToShow.filter((h) => !gridHandleSet.has(h));
              if (missingInGrid.length) {
                this.debug.log('off-page-fetch', { handles: missingInGrid });
                await this.loadOffPageProducts(missingInGrid, new Set(handlesToShow), handlesToShow);
              }
            }

            const showSet = new Set(handlesToShow);

            this.filtered = true;
            let hidden = 0;
            [...this.grid.querySelectorAll('li[data-product-handle]')].forEach((li) => {
              const h = li.getAttribute('data-product-handle');
              const show = showSet.has(h);
              this.setItemVisible(li, show);
              if (!show) hidden += 1;
            });

            this.pushDebugSnapshot({
              lastMatchCount: allMatches.length,
              lastVisible: this.grid.querySelectorAll('li[data-product-handle]').length - hidden,
              lastHidden: hidden
            });

            const visibleCount = this.grid.querySelectorAll('li[data-product-handle]:not(.nav-col-filter-hidden)').length;
            if (
              this.fullCatalogLoaded &&
              allMatches.length > 0 &&
              visibleCount === 0 &&
              !this.isSearchPage() &&
              !new URLSearchParams(window.location.search).get(NAV_JUMP_PARAM) &&
              this.navigateToFirstMatchingPage(allMatches)
            ) {
              return;
            }

            this.finishApply(allMatches.length, { resetPage });
          });
        } catch (error) {
          this.debug.error('apply-failed', { message: error.message, stack: error.stack });
          throw error;
        } finally {
          this._applying = false;
          if (this._pendingApply) {
            const next = this._pendingApply;
            this._pendingApply = null;
            this.apply(next);
          }
        }
      };

      try {
        return runApply();
      } catch (error) {
        this._applying = false;
        throw error;
      }
    }

    syncGridUi() {
      window.requestAnimationFrame(() => {
        if (window.bindCollectionProductCardInteractions) {
          window.bindCollectionProductCardInteractions(this.grid);
        } else if (window.initCollectionProductCardImages) {
          window.initCollectionProductCardImages(this.grid);
        }
        if (window.syncNavCollectionWishlistButtons) {
          window.syncNavCollectionWishlistButtons(this.grid);
        }
      });
    }

    finishApply(matchCount, { resetPage = true } = {}) {
      this.updateNoResults(matchCount);
      this.renderPagination();
      const page = resetPage ? 1 : this.getPage();
      this.updateUrl(page);
      this.updateResultsCount(matchCount);
      this.updateFilterCount();
      this.updateChips();
      this.pushDebugSnapshot({ lastApplyMatchCount: matchCount });
      this.syncGridUi();
    }

    async loadOffPageProducts(missingHandles, showSet, allMatches) {
      try {
        await this.ensureCatalogReady();
        await this.ensureGridHydrated();

        let matches = allMatches;
        if (!matches || !matches.length) {
          matches = this.getAllMatchingHandles();
        }

        if (!missingHandles.length) return;

        const uniqueMissing = [...new Set(missingHandles.filter(Boolean))];

        for (let i = 0; i < uniqueMissing.length; i += CARD_FETCH_BATCH) {
          const batch = uniqueMissing.slice(i, i + CARD_FETCH_BATCH);
          const stillMissing = batch.filter(
            (handle) => !this.grid.querySelector(`li[data-product-handle="${escapeSelector(handle)}"]`)
          );
          if (!stillMissing.length) continue;

          const html = await this.fetchCards(stillMissing);
          if (!html) {
            this.debug.warn('off-page-fetch-empty', { handles: stillMissing });
            continue;
          }
          this.grid.insertAdjacentHTML('beforeend', html);
        }
        this.refreshProductIndexFromDom();

        const matchSet = new Set(matches);
        this.grid.querySelectorAll('li[data-product-handle]').forEach((li) => {
          const h = li.getAttribute('data-product-handle');
          this.setItemVisible(li, matchSet.has(h) && showSet.has(h));
        });

        this.syncGridUi();
      } catch (error) {
        console.error('[NavColFilters] off-page load failed', error);
      }
    }

    enforceSearchResultsVisibility() {
      if (!this.isSearchPage() || !this.grid) return;
      this.grid.querySelectorAll('li[data-product-handle]').forEach((li) => {
        const h = li.getAttribute('data-product-handle');
        if (!h) return;
        const product = this.byHandle.get(h);
        const visible = product ? this.isInSearchResults(product) : false;
        this.setItemVisible(li, visible);
      });
      if (!this.active.size) {
        const count = parseInt(this.config && this.config.searchResultsCount, 10);
        if (Number.isFinite(count) && count >= 0) {
          this.updateResultsCount(count);
        }
      }
    }

    restore() {
      this.filtered = false;
      if (this.isSearchPage()) {
        this.enforceSearchResultsVisibility();
      } else {
        this.grid.querySelectorAll('li[data-product-handle]').forEach((li) => {
          this.setItemVisible(li, true);
        });
      }
      const pag = this.section.querySelector('.nav-col-client-pagination');
      if (pag) pag.remove();
      const resultCount = this.isSearchPage()
        ? (this.active.size > 0 ? this.getMatches().length : (parseInt(this.config && this.config.searchResultsCount, 10) || this.getSearchResultHandleSet().size))
        : this.products.length;
      this.updateNoResults(resultCount);
      this.updateUrl(1, true);
      this.updateResultsCount(resultCount);
      this.updateFilterCount();
      this.updateChips();
      this.syncGridUi();
    }

    async fetchCards(handles) {
      if (!handles.length || !this.config) return '';

      const unique = [...new Set(handles.map((h) => String(h || '').trim()).filter(Boolean))];
      if (!unique.length) return '';

      const productHtml = filterCardListItemsByHandles(await this.fetchCardsViaProductUrls(unique), unique);
      if (productHtml) return productHtml;

      let html = filterCardListItemsByHandles(await this.fetchCardsViaSections(unique), unique);
      if (!html && unique.length > 1) {
        const parts = [];
        for (const handle of unique) {
          const single = filterCardListItemsByHandles(await this.fetchCardsViaSections([handle]), [handle]);
          if (single) parts.push(single);
        }
        html = parts.join('');
      }
      if (!html) {
        html = filterCardListItemsByHandles(await this.fetchCardsViaSectionId(unique), unique);
      }
      if (!html && unique.length > 1) {
        const parts = [];
        for (const handle of unique) {
          const single = filterCardListItemsByHandles(await this.fetchCardsViaSectionId([handle]), [handle]);
          if (single) parts.push(single);
        }
        html = parts.join('');
      }
      return html;
    }

    async fetchCardsViaProductUrls(handles) {
      const parts = [];
      for (let i = 0; i < handles.length; i += PRODUCT_CARD_CONCURRENCY) {
        const chunk = handles.slice(i, i + PRODUCT_CARD_CONCURRENCY);
        const results = await Promise.all(chunk.map((handle) => this.fetchSingleProductCard(handle)));
        results.forEach((html) => {
          if (html) parts.push(html);
        });
      }
      return parts.join('');
    }

    async fetchSingleProductCard(handle) {
      const safeHandle = String(handle || '').trim();
      if (!safeHandle || !this.config) return '';

      const url = new URL('/products/' + encodeURIComponent(safeHandle), window.location.origin);
      url.searchParams.set('section_id', this.config.sectionApiId);
      url.searchParams.set('nav_col_single', '1');
      mergeEditorSearchParams(url.searchParams);

      try {
        const res = await fetch(url.toString());
        if (!res.ok) {
          this.debug.warn('single-card-fetch-http', { handle: safeHandle, status: res.status });
          return '';
        }
        const html = extractCardListItems(await res.text());
        if (!html) {
          this.debug.warn('single-card-fetch-parse', { handle: safeHandle });
        }
        return html;
      } catch (error) {
        this.debug.warn('single-card-fetch-failed', { handle: safeHandle, message: error.message });
        return '';
      }
    }

    async fetchCardsViaSections(handles) {
      const url = new URL(this.config.collectionUrl || window.location.pathname, window.location.origin);
      url.searchParams.set('sections', this.config.sectionApiId);
      url.searchParams.set('nav_col_render', '1');
      url.searchParams.set('filtered_handles', handles.join(','));
      url.searchParams.set('page', '1');
      mergeEditorSearchParams(url.searchParams);

      const res = await fetch(url.toString());
      if (!res.ok) return '';
      const data = await res.json();
      const html = data[this.config.sectionApiId] || Object.values(data)[0] || '';
      return extractCardListItems(html);
    }

    async fetchCollectionPageCards(page) {
      if (!this.config) return '';
      const url = new URL(this.config.collectionUrl || window.location.pathname, window.location.origin);
      const sort = new URLSearchParams(window.location.search).get('sort_by');
      url.searchParams.set('sections', this.config.sectionApiId);
      url.searchParams.set('page', String(page));
      if (sort) url.searchParams.set('sort_by', sort);
      mergeEditorSearchParams(url.searchParams);

      const res = await fetch(url.toString());
      if (!res.ok) return '';
      const data = await res.json();
      const html = data[this.config.sectionApiId] || Object.values(data)[0] || '';
      return extractCardListItems(html);
    }

    async fetchCardsViaSectionId(handles) {
      const url = new URL(this.config.collectionUrl || window.location.pathname, window.location.origin);
      url.searchParams.set('section_id', this.config.sectionApiId);
      url.searchParams.set('nav_col_render', '1');
      url.searchParams.set('filtered_handles', handles.join(','));
      url.searchParams.set('page', '1');
      mergeEditorSearchParams(url.searchParams);

      const res = await fetch(url.toString());
      if (!res.ok) return '';
      return extractCardListItems(await res.text());
    }

    /* ——— Filter UI build ——— */

    facetCounts() {
      const counts = {
        availability: { 'in-stock': 0, 'out-of-stock': 0 },
        vendor: new Map(),
        product_type: new Map(),
        size: new Map(),
        tag: new Map()
      };
      this.getCatalogPool().forEach((p) => {
        counts.availability[p.availability] += 1;
        if (p.vendor && !isHiddenFacetValue(p.vendor, p.vendorLabel)) {
          counts.vendor.set(p.vendor, (counts.vendor.get(p.vendor) || 0) + 1);
        }
        if (p.type) counts.product_type.set(p.type, (counts.product_type.get(p.type) || 0) + 1);
        p.sizes.forEach((s) => counts.size.set(s, (counts.size.get(s) || 0) + 1));
        p.tags.forEach((t) => counts.tag.set(t, (counts.tag.get(t) || 0) + 1));
      });
      return counts;
    }

    syncFilterOptions() {
      const counts = this.facetCounts();
      this.buildCheckboxGroup('vendor', counts.vendor, (h) => {
        const match = this.products.find((p) => p.vendor === h);
        const rawLabel = (match && match.vendorLabel) || h;
        return formatLabel(handleize(rawLabel));
      });
      this.buildCheckboxGroup('product_type', counts.product_type, (h) => {
        const match = this.products.find((p) => p.type === h);
        const rawLabel = (match && match.typeLabel) || h;
        return formatLabel(handleize(rawLabel));
      });
      this.buildCheckboxGroup('size', counts.size, (h) => formatSizeLabel(h), true);
      this.buildColorFilters();
      this.syncTagBoxes();
      this.updateAvailabilityCounts(counts.availability);
      this.syncGroupVisibility();
      this._optionsBuilt = true;
    }

    setFilterLabel(labelEl, base, count) {
      return this.setFilterLabelWithCount(labelEl, base, count, true);
    }

    setFilterLabelWithCount(labelEl, base, count, showCount) {
      if (!labelEl) return;
      const baseText = String(base || '').trim();
      const safeBase = escapeHtml(baseText);
      const n = Number.isFinite(count) ? count : 0;
      labelEl.dataset.filterLabelBase = baseText;
      labelEl.innerHTML = `<span class="filter-label-text">${safeBase}</span>`;
      if (showCount !== false) {
        labelEl.innerHTML += `<span class="filter-label-count">(${n})</span>`;
      }
    }

    showCountEnabled(container) {
      return !(container && container.dataset && container.dataset.showCount === 'false');
    }

    buildCheckboxGroup(name, countMap, labelFn, naturalSort) {
      const container = this.filtersEl.querySelector(`[data-nav-col-filter="${name}"]`);
      if (!container || container.dataset.serverRendered === 'true') return;
      const showCount = this.showCountEnabled(container);

      const checked = new Set();
      this.filtersEl.querySelectorAll(`input[name="${name}"]:checked`).forEach((i) => checked.add(i.value));
      const activeSet = this.active.get(name);
      if (activeSet) {
        activeSet.forEach((value) => checked.add(value));
      }

      const entries = [...countMap.entries()].filter(([value, c]) => {
        if (c <= 0) return false;
        if (name === 'vendor') {
          const match = this.products.find((p) => p.vendor === value);
          const rawLabel = (match && match.vendorLabel) || value;
          return !isHiddenFacetValue(value, rawLabel);
        }
        return true;
      });
      if (name === 'size') {
        entries.sort((a, b) => compareSizeValues(a[0], b[0]));
      } else if (naturalSort) {
        entries.sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
      } else {
        entries.sort((a, b) => labelFn(a[0]).localeCompare(labelFn(b[0])));
      }

      container.innerHTML = '';
      entries.forEach(([value, count]) => {
        const label = document.createElement('label');
        const isProductType = name === 'product_type';
        const isSize = name === 'size';
        label.className = isProductType
          ? 'filter-option filter-option--tag-box'
          : isSize
            ? 'filter-option filter-option--size-box'
            : 'filter-option';
        const labelText = labelFn(value);
        label.innerHTML =
          `<input type="checkbox" name="${name}" value="${value}" class="filter-checkbox"` +
          (checked.has(value) ? ' checked' : '') +
          `><span class="filter-label" data-filter-label-base="${escapeHtml(labelText)}"></span>`;
        this.setFilterLabelWithCount(label.querySelector('.filter-label'), labelText, count, showCount);
        label.classList.toggle('is-selected', checked.has(value));
        container.appendChild(label);
      });

      if (name === 'product_type') {
        container.classList.add('tag-box-filter-options');
      }
    }

    buildColorFilters() {
      const container = this.filtersEl.querySelector('[data-nav-col-filter="color"]');
      if (!container || container.dataset.serverRendered === 'true') return;
      const showCount = this.showCountEnabled(container);

      const checked = new Set();
      this.filtersEl.querySelectorAll('input[name="color"]:checked').forEach((i) => checked.add(i.value));
      const grouped = new Set();
      const groups = (this.config && this.config.colorGroups) || [];

      container.innerHTML = '';

      groups.forEach((group) => {
        const handles = String(group.value || '')
          .split('|')
          .map(normalizeColorKey)
          .filter(Boolean);
        const count = this.products.filter((p) =>
          handles.some((h) => p.colors.includes(h))
        ).length;
        if (!count) return;
        handles.forEach((h) => grouped.add(h));
        const label = document.createElement('label');
        label.className = 'filter-option filter-option--color';
        label.innerHTML =
          `<input type="checkbox" name="color" value="${group.value}" class="filter-checkbox"` +
          (checked.has(group.value) ? ' checked' : '') +
          `><span class="color-swatch" style="${group.swatchStyle || ''}"></span><span class="filter-label"></span>`;
        this.setFilterLabelWithCount(label.querySelector('.filter-label'), group.label, count, showCount);
        container.appendChild(label);
      });

      const solo = new Map();
      this.products.forEach((p) => {
        p.colors.forEach((c) => {
          if (!grouped.has(c)) solo.set(c, (solo.get(c) || 0) + 1);
        });
      });

      [...solo.entries()]
        .sort((a, b) => formatLabel(a[0]).localeCompare(formatLabel(b[0])))
        .forEach(([value, count]) => {
          const label = document.createElement('label');
          label.className = 'filter-option filter-option--color';
          label.innerHTML =
            `<input type="checkbox" name="color" value="${value}" class="filter-checkbox"` +
            (checked.has(value) ? ' checked' : '') +
            `><span class="color-swatch" style="background-color:${formatLabel(value)};"></span><span class="filter-label"></span>`;
          this.setFilterLabelWithCount(label.querySelector('.filter-label'), formatLabel(value), count, showCount);
          container.appendChild(label);
        });
    }

    syncTagBoxes() {
      (this.config.tagFilterGroups || []).forEach((group) => {
        const key = 'tag_' + group.id;
        const container = this.filtersEl.querySelector(`[data-nav-col-filter="${key}"]`);
        if (!container) return;
        const showCount = this.showCountEnabled(container);

        container.querySelectorAll('.filter-checkbox').forEach((checkbox) => {
          const tagHandle = handleize(checkbox.value);
          const count = this.getCatalogPool().filter((p) => p.tags.includes(tagHandle)).length;
          const shouldHide = count === 0;
          const option = checkbox.closest('.filter-option');
          if (shouldHide) {
            checkbox.checked = false;
          }
          checkbox.disabled = shouldHide;
          if (option) {
            option.hidden = shouldHide;
            option.style.display = shouldHide ? 'none' : '';
          }
          const label = option ? option.querySelector('.filter-label') : null;
          if (label) {
            const base = label.dataset.filterLabelBase || formatLabel(tagHandle);
            this.setFilterLabelWithCount(label, base, count, showCount);
          }
        });
      });
    }

    updateAvailabilityCounts(availability) {
      const container = this.filtersEl.querySelector('[data-nav-col-filter="availability"]');
      const showCount = this.showCountEnabled(container);
      this.filtersEl.querySelectorAll('input[name="availability"]').forEach((input) => {
        const label = input.closest('label')?.querySelector('.filter-label');
        if (!label) return;
        const base = input.value === 'in-stock' ? 'In Stock' : 'Out of Stock';
        const count = availability[input.value] || 0;
        this.setFilterLabelWithCount(label, base, count, showCount);
        const option = input.closest('.filter-option');
        if (option) option.hidden = count === 0;
      });
    }

    syncGroupVisibility() {
      this.filtersEl.querySelectorAll('.filter-group').forEach((group) => {
        if (group.querySelector('.price-filter-container')) return;
        if (group.classList.contains('filter-group--custom-tag-filter')) {
          const options = [...group.querySelectorAll('.filter-option')].filter((o) => !o.hidden);
          group.hidden = options.length === 0;
          group.style.display = options.length === 0 ? 'none' : '';
          return;
        }
        const options = [...group.querySelectorAll('.filter-option')].filter((o) => !o.hidden);
        group.hidden = options.length === 0;
      });
    }

    /* ——— Chips, counts, URL ——— */

    updateChips() {
      if (!this.tagsEl) return;
      this.tagsEl.innerHTML = '';
      const activeForDisplay = this.active.size ? this.active : this.collectActiveFromDom();
      if (!activeForDisplay.size) {
        this.tagsEl.hidden = true;
        return;
      }
      if (!this.active.size && activeForDisplay.size) {
        this.active = activeForDisplay;
      }
      this.tagsEl.hidden = false;

      const header = document.createElement('div');
      header.className = 'nav-col-active-filters__header';
      const heading = document.createElement('p');
      heading.className = 'nav-col-active-filters__label';
      heading.textContent = 'REFINE BY';
      header.appendChild(heading);

      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'nav-col-active-filters__clear';
      clearBtn.textContent = 'Clear All';
      clearBtn.addEventListener('click', () => this.clearAll());
      header.appendChild(clearBtn);
      this.tagsEl.appendChild(header);

      activeForDisplay.forEach((values, key) => {
        if (key === 'price') {
          const { min, max } = values;
          let text = '';
          if (min > 0 && max < Infinity) text = `$${min} - $${max}`;
          else if (min > 0) text = `Over $${min}`;
          else if (max < Infinity) text = `Under $${max}`;
          if (text) this.appendChip('Price', text, key, null);
          return;
        }
        const group = (this.config.tagFilterGroups || []).find((g) => 'tag_' + g.id === key);
        Array.from(values).forEach((value) => {
          if (group && group.layout === 'category_box') {
            const input = this.filtersEl.querySelector(
              `input[name="${escapeSelector(key)}"][value="${escapeSelector(value)}"]`
            );
            const inputLabel = input?.closest('.filter-option')?.querySelector('.filter-label');
            const display = (inputLabel?.dataset.filterLabelBase || value || group.title || '').trim();
            this.appendChip('', display, key, value);
          } else {
            this.appendChip(this.labelForKey(key), value, key, value);
          }
        });
      });
    }

    appendChip(label, value, key, filterValue) {
      const tag = document.createElement('div');
      tag.className = 'filter-tag';
      tag.innerHTML = label
        ? `<span class="filter-tag-label">${label}:</span><span class="filter-tag-value">${value}</span><button type="button" class="filter-tag-remove" aria-label="Remove filter"></button>`
        : `<span class="filter-tag-value">${value}</span><button type="button" class="filter-tag-remove" aria-label="Remove filter"></button>`;
      tag.querySelector('.filter-tag-remove').addEventListener('click', () => {
        if (filterValue != null) {
          const input = this.filtersEl.querySelector(
            `input[name="${escapeSelector(key)}"][value="${escapeSelector(filterValue)}"]`
          );
          if (input) input.checked = false;
        } else {
          this.active.delete(key);
          const minEl = this.filtersEl.querySelector('input[name="price_min"]');
          const maxEl = this.filtersEl.querySelector('input[name="price_max"]');
          if (minEl) minEl.value = '';
          if (maxEl) maxEl.value = '';
        }
        this.scheduleApply({ resetPage: true });
      });
      this.tagsEl.appendChild(tag);
    }

    labelForKey(key) {
      if (key === 'vendor') return 'Brand';
      if (key === 'product_type') return 'Product Type';
      if (key === 'size') return 'Size';
      if (key === 'color') return 'Color';
      if (key === 'availability') return 'Availability';
      const group = (this.config.tagFilterGroups || []).find((g) => 'tag_' + g.id === key);
      return (group && group.title) || 'Filter';
    }

    updateFilterCount() {
      const el = this.filtersEl.querySelector('.filter-count');
      if (!el) return;
      let n = 0;
      this.active.forEach((v) => {
        n += v instanceof Set ? v.size : 1;
      });
      if (n > 0) {
        el.textContent = '(' + n + ')';
        el.style.display = 'inline';
      } else {
        el.style.display = 'none';
      }
    }

    updateResultsCount(count) {
      if (!this.resultsEl) return;
      const n = typeof count === 'number' ? count : this.products.length;
      this.resultsEl.textContent = n + (n === 1 ? ' product' : ' products');
    }

    updateNoResults(count) {
      if (!this.noResultsEl) return;
      if (count === 0) {
        this.noResultsEl.style.display = 'block';
      } else {
        this.noResultsEl.style.display = 'none';
      }
    }

    getPage() {
      const p = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
      return Number.isFinite(p) && p > 0 ? p : 1;
    }

    buildUrlParams(page, clearFilters) {
      const params = new URLSearchParams();
      const current = new URLSearchParams(window.location.search);
      if (this.isSearchPage()) {
        const q = current.get('q');
        if (q) params.set('q', q);
        const type = current.get('type');
        if (type) params.set('type', type);
        const prefix = current.get('options[prefix]');
        if (prefix) params.set('options[prefix]', prefix);
      }
      const sort = current.get('sort_by');
      if (sort) params.set('sort_by', sort);
      if (!clearFilters) {
        if (page > 1) params.set('page', String(page));
        this.active.forEach((values, key) => {
          if (key === 'price') {
            if (values.min > 0) params.set('price_min', values.min);
            if (values.max < Infinity) params.set('price_max', values.max);
          } else {
            params.set(key, Array.from(values).join(','));
          }
        });
      }
      params.delete(NAV_JUMP_PARAM);
      return mergeEditorSearchParams(params);
    }

    updateUrl(page, clearFilters) {
      const params = this.buildUrlParams(page, clearFilters);
      const q = params.toString();
      const path = window.location.pathname;
      window.history.replaceState({}, '', q ? path + '?' + q : path);
    }

    navigateToFirstMatchingPage(allMatches) {
      if (!Array.isArray(allMatches) || allMatches.length === 0) return false;
      const firstHandle = allMatches[0];
      const index = this.products.findIndex((p) => p && p.handle === firstHandle);
      if (index < 0) return false;

      const perPageRaw = parseInt(this.config && this.config.productsPerPage, 10);
      const perPage = Number.isFinite(perPageRaw) && perPageRaw > 0 ? perPageRaw : 16;
      const targetPage = Math.floor(index / perPage) + 1;
      const currentPage = this.getPage();
      if (!targetPage || targetPage === currentPage) return false;

      const params = this.buildUrlParams(targetPage, false);
      params.set(NAV_JUMP_PARAM, '1');
      const q = params.toString();
      const path = window.location.pathname;
      window.location.assign(q ? path + '?' + q : path);
      return true;
    }

    loadFromUrl() {
      const params = new URLSearchParams(window.location.search);
      if (isThemeEditor()) {
        // Theme editor previewPath can carry stale filter params from prior sessions.
        // Always start unfiltered in editor so products are never hidden by persisted state.
        this.active.clear();
        this.restore();
        return;
      }
      let has = false;
      this.filtersEl.querySelectorAll('.filter-checkbox').forEach((input) => {
        const vals = (params.get(input.name) || '').split(',').filter(Boolean);
        if (vals.includes(input.value)) {
          input.checked = true;
          has = true;
        }
      });
      const min = params.get('price_min');
      const max = params.get('price_max');
      if (min) {
        const el = this.filtersEl.querySelector('input[name="price_min"]');
        if (el) el.value = min;
        has = true;
      }
      if (max) {
        const el = this.filtersEl.querySelector('input[name="price_max"]');
        if (el) el.value = max;
        has = true;
      }
      if (has) {
        this.debug.log('load-from-url', {
          params: Object.fromEntries([...params.entries()].filter(([k]) => k !== 'sort_by'))
        });
        this.readActiveFromDom();
        this.apply({ resetPage: false });
      } else {
        // In theme editor previews, previous filter state can linger in DOM classes.
        // Ensure the grid is fully reset when URL has no active filters.
        this.active.clear();
        this.restore();
      }
    }

    renderPagination() {
      this.section.querySelectorAll('.nav-col-client-pagination').forEach((el) => el.remove());
    }

    /* ——— Events ——— */

    bindEvents() {
      const onChange = (e) => {
        if (this._suppress) return;
        if (!e.target.classList.contains('filter-checkbox')) return;
        this.debug.log('filter-change', { name: e.target.name, value: e.target.value, checked: e.target.checked });
        if (isMobile()) {
          this.readActiveFromDom();
          this.updateFilterCount();
          this.updateChips();
          return;
        }
        this.scheduleApply({ resetPage: true });
      };
      this.filtersEl.addEventListener('change', onChange);
      this.track(() => this.filtersEl.removeEventListener('change', onChange));

      const priceBtn = this.filtersEl.querySelector('.apply-price-btn');
      if (priceBtn) {
        const onPrice = () => {
          const minEl = this.filtersEl.querySelector('input[name="price_min"]');
          const maxEl = this.filtersEl.querySelector('input[name="price_max"]');
          const min = minEl ? parseFloat(minEl.value) || 0 : 0;
          const max = maxEl ? parseFloat(maxEl.value) || Infinity : Infinity;
          if (min > 0 || max < Infinity) this.active.set('price', { min, max });
          else this.active.delete('price');
          if (isMobile()) {
            this.updateFilterCount();
            this.updateChips();
            return;
          }
          this.applyFilters({ resetPage: true });
        };
        priceBtn.addEventListener('click', onPrice);
        this.track(() => priceBtn.removeEventListener('click', onPrice));
      }

      const clearBtn = this.filtersEl.querySelector('.clear-filters-btn');
      if (clearBtn) {
        const onClear = () => this.clearAll();
        clearBtn.addEventListener('click', onClear);
        this.track(() => clearBtn.removeEventListener('click', onClear));
      }

      const clearLink = this.section.querySelector('.clear-all-filters-link');
      if (clearLink) {
        const onClearLink = () => this.clearAll();
        clearLink.addEventListener('click', onClearLink);
        this.track(() => clearLink.removeEventListener('click', onClearLink));
      }

    }
  }

  NavColFilters.collectFilterParamKeys = collectFilterParamKeys;
  NavColFilters.mergeEditorSearchParams = mergeEditorSearchParams;

  window.NavColFilters = NavColFilters;
})();
